import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Info } from "lucide-react";
import { Employee } from "../types/employee";

interface EmployeeDetailsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  employee: Employee | null;
}

type FilterPeriod = "all" | "today" | "week" | "month" | "year";

const EmployeeDetailsSidebar: React.FC<EmployeeDetailsSidebarProps> = ({
  isOpen,
  onClose,
  employee,
}) => {
  const [activeTab, setActiveTab] = useState<"overview" | "personal" | "workspace" | "pay">("overview");
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>("all");

  if (!employee) {
    return null;
  }

  // Get all employee bookings
  const allEmployeeBookings = (employee as any)?.employeeBookings || [];

  // Filter bookings based on selected period
  const filterBookingsByPeriod = (bookings: any[], period: FilterPeriod): any[] => {
    if (period === "all") {
      return bookings;
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return bookings.filter((booking: any) => {
      // Use created_at first, fallback to updated_at
      const bookingDateStr = booking.created_at || booking.updated_at;

      // If no date available, exclude from date-based filters
      if (!bookingDateStr) {
        return false;
      }

      const bookingDate = new Date(bookingDateStr);
      bookingDate.setHours(0, 0, 0, 0);

      switch (period) {
        case "today":
          return bookingDate.getTime() === today.getTime();

        case "week": {
          // Get the start of the current week (Monday)
          const dayOfWeek = now.getDay();
          const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust when day is Sunday
          const weekStart = new Date(now.getFullYear(), now.getMonth(), diff);
          weekStart.setHours(0, 0, 0, 0);
          return bookingDate >= weekStart;
        }

        case "month": {
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          return bookingDate >= monthStart;
        }

        case "year": {
          const yearStart = new Date(now.getFullYear(), 0, 1);
          return bookingDate >= yearStart;
        }

        default:
          return true;
      }
    });
  };

  // Get filtered bookings
  const employeeBookings = filterBookingsByPeriod(allEmployeeBookings, filterPeriod);

  // Calculate total sales by summing all amounts from filtered bookings
  const totalSales = employeeBookings.reduce((sum: number, booking: any) => {
    return sum + (parseFloat(booking.amount) || 0);
  }, 0);

  // Calculate unique appointments by counting unique booking_ids from filtered bookings
  const uniqueBookingIds = new Set(
    employeeBookings
      .map((booking: any) => booking.booking_id)
      .filter((id: any) => id !== null && id !== undefined)
  );
  const totalAppointments = uniqueBookingIds.size;

  // Get filter period label for display
  const getFilterLabel = (period: FilterPeriod): string => {
    switch (period) {
      case "all":
        return "Total sales from all bookings";
      case "today":
        return "Sales from today";
      case "week":
        return "Sales from week to date";
      case "month":
        return "Sales from this month";
      case "year":
        return "Sales from this year";
      default:
        return "Total sales from bookings";
    }
  };

  // Get appointments label for display
  const getAppointmentsLabel = (period: FilterPeriod): string => {
    switch (period) {
      case "all":
        return "Total bookings";
      case "today":
        return "Bookings from today";
      case "week":
        return "Bookings from week to date";
      case "month":
        return "Bookings from this month";
      case "year":
        return "Bookings from this year";
      default:
        return "Total bookings";
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const formatMobile = (mobile: string | null, callingCode: string | null) => {
    if (!mobile) return "Not provided";
    if (callingCode) return `${callingCode} ${mobile}`;
    return mobile;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px] z-[99]"
          />

          {/* Sidebar Panel */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3, ease: "easeOut" }}
            className="relative h-screen w-full lg:w-[85vw] lg:max-w-5xl bg-white shadow-[-10px_0_30px_rgba(0,0,0,0.05)] border-l border-gray-100 z-[101] flex flex-col lg:flex-row origin-right"
          >
            {/* Standardized Close Button (Matches QuickPaymentPanel style) */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="absolute lg:-left-14 right-4 top-4 lg:right-auto lg:top-8 w-10 h-10 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-md lg:shadow-[-4px_0_12px_rgba(0,0,0,0.1)] hover:bg-[#402B69] hover:text-white transition-all z-[102] group"
              aria-label="Close"
            >
              <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
            </motion.button>

            <div className="relative h-full w-full flex flex-col lg:flex-row">
              {/* Left panel styled similar to existing sidebar */}
              <div className="relative w-full lg:w-[30%] border-b lg:border-r bg-white pt-16 lg:pt-0">
                <div className="hidden lg:block absolute top-0 right-0 h-full w-[2px] bg-gray-200 shadow-[4px_0_12px_rgba(0,0,0,0.15)]"></div>

                {/* Employee Header */}
                <div className="p-4 lg:p-6 border-b border-gray-200 bg-gray-50/50 lg:bg-transparent">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-[#F1ECFB] border border-[#C4A6E1] flex items-center justify-center text-[#885ABB] font-bold text-lg overflow-hidden shadow-sm">
                      {employee.image && employee.image !== "https://salonify.co.uk/img/default-avatar-user.png" ? (
                        <img src={employee.image} alt={employee.name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        getInitials(employee.name)
                      )}
                    </div>
                    <div>
                      <h2 className="text-base lg:text-[18px] font-bold lg:font-semibold text-[#402B69]">{employee.name}</h2>
                      <p className="text-sm lg:text-[14px] font-medium text-[#885ABB]">{employee.role}</p>
                    </div>
                  </div>
                </div>

                {/* Navigation Tabs */}
                <div className="flex flex-row lg:flex-col text-sm lg:text-[15px] font-bold lg:font-medium overflow-x-auto scrollbar-hide py-2 lg:py-8 lg:mt-0">
                  {[
                    { id: "overview", label: "Overview" },
                    { id: "personal", label: "Personal" },
                    { id: "workspace", label: "Workspace" },
                    { id: "pay", label: "Pay" },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`relative flex items-center px-6 lg:px-4 py-3 lg:py-2 text-left transition-all duration-200 group whitespace-nowrap ${activeTab === tab.id ? "text-[#885ABB] font-bold lg:font-semibold border-b-2 lg:border-b-0 border-[#885ABB]" : "text-gray-600 lg:text-gray-800 hover:text-[#885ABB]"
                        }`}
                    >
                      <span className={`lg:ml-5 transition-all duration-200 ${activeTab === tab.id ? "translate-x-0" : "lg:group-hover:translate-x-1"}`}>
                        {tab.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Right panel content */}
              <div className="flex-1 px-4 lg:px-10 py-6 lg:py-8 overflow-y-auto bg-gray-100">
                {activeTab === "overview" && (
                  <div className="space-y-5">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-[22px] font-semibold text-[#402B69]">Overview</h2>
                    </div>

                    {/* Performance Dashboard */}
                    <div className="bg-white rounded-lg p-6 border border-[#C4A6E1] shadow-sm">
                      <div className="flex items-center justify-between mb-6">
                        <h4 className="text-[18px] font-semibold text-[#402B69]">Performance dashboard</h4>
                        <div className="flex items-center gap-3">
                          <select
                            value={filterPeriod}
                            onChange={(e) => setFilterPeriod(e.target.value as FilterPeriod)}
                            className="text-[14px] border border-[#C4A6E1] rounded-md px-3 py-2 bg-white text-[#402B69] focus:outline-none focus:ring-2 focus:ring-[#885ABB] cursor-pointer"
                          >
                            <option value="all">All time</option>
                            <option value="today">Today</option>
                            <option value="week">Week to date</option>
                            <option value="month">This month</option>
                            <option value="year">This year</option>
                          </select>
                        </div>
                      </div>

                      {/* Metrics Cards */}
                      <div className="grid grid-cols-1 gap-4">
                        {/* Sales Card */}
                        <div className="bg-[#F9F9F9] rounded-lg p-4 border border-[#BBB9F3]">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <h5 className="text-[16px] font-semibold text-[#402B69]">Sales</h5>
                              <Info size={16} className="text-[#885ABB]" />
                            </div>
                          </div>
                          <div className="text-[24px] font-bold text-[#402B69]">
                            £{totalSales.toFixed(2)}
                          </div>
                          <div className="text-[14px] text-[#885ABB] mt-1 font-medium">
                            {getFilterLabel(filterPeriod)}
                          </div>
                        </div>

                        {/* Appointments Card */}
                        <div className="bg-[#F9F9F9] rounded-lg p-4 border border-[#BBB9F3]">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <h5 className="text-[16px] font-semibold text-[#402B69]">Appointments</h5>
                              <Info size={16} className="text-[#885ABB]" />
                            </div>
                          </div>
                          <div className="text-[24px] font-bold text-[#402B69]">
                            {totalAppointments}
                          </div>
                          <div className="text-[14px] text-[#885ABB] mt-1 font-medium">
                            {getAppointmentsLabel(filterPeriod)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "personal" && (
                  <div className="space-y-5">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-[22px] font-semibold text-[#402B69]">Personal Information</h2>
                    </div>
                    <div className="bg-white rounded-lg p-6 border border-[#C4A6E1] shadow-sm">
                      <div className="space-y-5">
                        <div>
                          <label className="text-[14px] font-medium text-[#402B69]">Full Name</label>
                          <p className="text-[16px] text-[#402B69] mt-1">{employee.name}</p>
                        </div>
                        <div>
                          <label className="text-[14px] font-medium text-[#402B69]">Email</label>
                          <p className="text-[16px] text-[#402B69] mt-1">{employee.email}</p>
                        </div>
                        <div>
                          <label className="text-[14px] font-medium text-[#402B69]">Mobile</label>
                          <p className="text-[16px] text-[#402B69] mt-1">
                            {formatMobile(employee.mobile, employee.calling_code)}
                          </p>
                        </div>
                        {employee.group && (
                          <div>
                            <label className="text-[14px] font-medium text-[#402B69]">Group</label>
                            <p className="text-[16px] text-[#402B69] mt-1">{employee.group}</p>
                          </div>
                        )}
                        {employee.phone && (
                          <div>
                            <label className="text-[14px] font-medium text-[#402B69]">Phone</label>
                            <p className="text-[16px] text-[#402B69] mt-1">{employee.phone}</p>
                          </div>
                        )}
                        {employee.address && (
                          <div>
                            <label className="text-[14px] font-medium text-[#402B69]">Address</label>
                            <p className="text-[16px] text-[#402B69] mt-1">{employee.address}</p>
                          </div>
                        )}
                        {employee.country && (
                          <div>
                            <label className="text-[14px] font-medium text-[#402B69]">Country</label>
                            <p className="text-[16px] text-[#402B69] mt-1">{employee.country}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "workspace" && (
                  <div className="space-y-5">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-[22px] font-semibold text-[#402B69]">Workspace</h2>
                    </div>
                    <div className="bg-white rounded-lg p-6 border border-[#C4A6E1] shadow-sm">
                      <div className="space-y-5">
                        <div>
                          <label className="text-[14px] font-medium text-[#402B69]">Role</label>
                          <p className="text-[16px] text-[#402B69] mt-1">{employee.role}</p>
                        </div>
                        {employee.status && (
                          <div>
                            <label className="text-[14px] font-medium text-[#402B69]">Status</label>
                            <div className="mt-1">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-[14px] font-medium capitalize ${employee.status === "active"
                                ? "bg-green-100 text-green-700 border border-green-200"
                                : "bg-gray-200 text-gray-600 border border-gray-300"
                                }`}>
                                {employee.status}
                              </span>
                            </div>
                          </div>
                        )}
                        <div>
                          <label className="text-[14px] font-medium text-[#402B69]">Assigned Services</label>
                          <div className="mt-2">
                            {employee.services && employee.services.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {employee.services.map((service, index) => (
                                  <span key={index} className="px-3 py-1 bg-[#F1ECFB] text-[#885ABB] rounded-full text-[14px] font-medium border border-[#C4A6E1]">
                                    {service}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p className="text-[#885ABB] text-[14px]">No services assigned</p>
                            )}
                          </div>
                        </div>
                        {employee.locations && employee.locations.length > 0 && (
                          <div>
                            <label className="text-[14px] font-medium text-[#402B69]">Locations</label>
                            <div className="mt-2">
                              <div className="flex flex-wrap gap-2">
                                {employee.locations.map((location, index) => (
                                  <span key={index} className="px-3 py-1 bg-[#F1ECFB] text-[#885ABB] rounded-full text-[14px] font-medium border border-[#C4A6E1]">
                                    {location.name || location}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "pay" && (
                  <div className="space-y-5">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-[22px] font-semibold text-[#402B69]">Pay Information</h2>
                    </div>
                    <div className="bg-white rounded-lg p-6 border border-[#C4A6E1] shadow-sm">
                      <div className="space-y-5">
                        <div>
                          <label className="text-[14px] font-medium text-[#402B69]">Hourly Rate</label>
                          <p className="text-[16px] text-[#402B69] mt-1">
                            {(employee as any)?.employeeDetails?.rate && (employee as any)?.employeeDetails?.work_type === "hourly"
                              ? `£${parseFloat((employee as any).employeeDetails.rate).toFixed(2)}`
                              : "Not configured"}
                          </p>
                        </div>
                        <div>
                          <label className="text-[14px] font-medium text-[#402B69]">Commission</label>
                          <p className="text-[16px] text-[#402B69] mt-1">
                            {(employee as any)?.employeeDetails?.commission
                              ? `${(employee as any).employeeDetails.commission}%`
                              : "no commission"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EmployeeDetailsSidebar;
